import React, { useEffect, useState, useRef } from 'react';
import { costApi, CostFilters, CreateCostData } from '../services/costApi';
import { farmApi } from '../services/api';
import { periodApi } from '../services/periodApi';
import { Cost, Farm, CostAllocation, Period } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { handleApiError, showError, showSuccess } from '../utils/handleApiError';
import AnimalSelectorModal, { SelectedAnimal } from '../components/AnimalSelectorModal';
import ClearableSelect from '../components/ClearableSelect';
import Pagination from '../components/Pagination';
import ConfirmModal from '../components/ConfirmModal';

const Costs: React.FC = () => {
  const { t } = useLanguage();
  const [costs, setCosts] = useState<Cost[]>([]);
  const [showAnimalSelector, setShowAnimalSelector] = useState(false);
  const [animalDisplay, setAnimalDisplay] = useState('');
  const [farms, setFarms] = useState<Farm[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [activePeriod, setActivePeriod] = useState<Period | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [editingCost, setEditingCost] = useState<Cost | null>(null);
  const [selectedCost, setSelectedCost] = useState<Cost | null>(null);
  const [allocations, setAllocations] = useState<CostAllocation[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<CostFilters>({});
  const [confirmAction, setConfirmAction] = useState<{ action: 'approve' | 'delete' | 'allocate' | 'deleteAllocations'; id: number } | null>(null);
  const [newCost, setNewCost] = useState<Partial<Cost>>({
    cost_level: 'farm',
    category: '',
    description: '',
    amount: 0,
    cost_date: new Date().toISOString().split('T')[0],
    allocation_method: 'darabszam',
    approved: false,
    notes: '',
  });

  // Load dropdown data once
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const [farmsResponse, periodsResponse] = await Promise.all([
          farmApi.getAll(),
          periodApi.getPeriods(),
        ]);
        setFarms(Array.isArray(farmsResponse) ? farmsResponse : []);
        setPeriods(Array.isArray(periodsResponse) ? periodsResponse : []);

        try {
          const active = await periodApi.getActive();
          setActivePeriod(active);
        } catch {
          setActivePeriod(null);
        }
      } catch (error) {
        console.error('Error loading dropdown data:', error);
        handleApiError(error, 'Hiba a legördülő adatok betöltése során');
      }
    };
    loadDropdownData();
  }, []);

  const prevDeps = useRef({ filters });
  const isInitialMount = useRef(true);

  useEffect(() => {
    const filtersChanged = !isInitialMount.current && prevDeps.current.filters !== filters;
    prevDeps.current = { filters };
    isInitialMount.current = false;

    if (filtersChanged && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    loadCosts();
  }, [currentPage, filters]);

  const loadCosts = async () => {
    try {
      setLoading(true);
      const costsResponse = await costApi.getCosts({ ...filters, page: currentPage });
      setCosts(Array.isArray(costsResponse.data) ? costsResponse.data : []);
      setCurrentPage(costsResponse.current_page);
      setLastPage(costsResponse.last_page);
      setTotal(costsResponse.total);
    } catch (error) {
      console.error('Error loading costs:', error);
      handleApiError(error, 'Hiba az adatok betöltése során');
      setCosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAnimalSelected = (animals: SelectedAnimal[]) => {
    const animal = animals[0];
    setNewCost(prev => ({ ...prev, animal_id: animal.id }));
    setAnimalDisplay(`${animal.ear_tag}${animal.breed ? ` - ${animal.breed}` : ''}`);
    setShowAnimalSelector(false);
  };

  const handleAddCost = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const costData: CreateCostData = {
        period_id: newCost.period_id,
        cost_level: newCost.cost_level as 'farm' | 'box' | 'animal',
        category: newCost.category || '',
        description: newCost.description || '',
        amount: parseFloat(newCost.amount?.toString() || '0'),
        cost_date: newCost.cost_date || new Date().toISOString().split('T')[0],
        allocation_method: newCost.allocation_method as 'darabszam' | 'suly' | 'egyedi',
        farm_id: newCost.cost_level === 'farm' ? newCost.farm_id : undefined,
        box_id: newCost.cost_level === 'box' ? newCost.box_id : undefined,
        animal_id: newCost.cost_level === 'animal' ? newCost.animal_id : undefined,
        invoice_number: newCost.invoice_number,
        supplier: newCost.supplier,
        notes: newCost.notes,
      };

      await costApi.createCost(costData);
      showSuccess('Költség sikeresen létrehozva');
      setShowAddForm(false);
      setNewCost({
        cost_level: 'farm',
        category: '',
        description: '',
        amount: 0,
        cost_date: new Date().toISOString().split('T')[0],
        allocation_method: 'darabszam',
        approved: false,
        notes: '',
      });
      setAnimalDisplay('');
      loadCosts();
    } catch (error) {
      console.error('Error creating cost:', error);
      handleApiError(error, 'Hiba a költség létrehozása során');
    }
  };

  const handleApproveCost = async (costId: number) => {
    try {
      await costApi.approveCost(costId);
      showSuccess('Költség jóváhagyva');
      loadCosts();
    } catch (error) {
      console.error('Error approving cost:', error);
      handleApiError(error, 'Hiba a jóváhagyás során');
    }
  };

  const handleEditCost = (cost: Cost) => {
    setEditingCost(cost);
    setShowEditForm(true);
  };

  const handleUpdateCost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCost) return;

    try {
      const costData = {
        category: editingCost.category,
        description: editingCost.description,
        amount: parseFloat(editingCost.amount?.toString() || '0'),
        cost_date: editingCost.cost_date,
        invoice_number: editingCost.invoice_number,
        allocation_method: editingCost.allocation_method,
        notes: editingCost.notes,
      };

      await costApi.updateCost(editingCost.id, costData);
      setShowEditForm(false);
      setEditingCost(null);
      showSuccess('Költség sikeresen frissítve');
      loadCosts();
    } catch (error) {
      console.error('Error updating cost:', error);
      handleApiError(error, 'Hiba történt a költség frissítése során');
    }
  };

  const handleDeleteCost = async (costId: number) => {
    try {
      await costApi.deleteCost(costId);
      showSuccess('Költség sikeresen törölve');
      loadCosts();
    } catch (error) {
      console.error('Error deleting cost:', error);
      handleApiError(error, 'Hiba történt a költség törlése során');
    }
  };

  const handleAllocateCost = async (cost: Cost) => {
    if (cost.is_allocated) {
      showError('Ez a költség már fel van osztva');
      return;
    }

    setConfirmAction({ action: 'allocate', id: cost.id });
  };

  const executeAllocateCost = async (costId: number) => {
    try {
      const result = await costApi.allocateCost(costId);
      showSuccess(`Költség allokálva: ${result.data.animal_count} állat, összesen: ${result.data.total_allocated} Ft`);
      loadCosts();
    } catch (error: any) {
      console.error('Error allocating cost:', error);
      handleApiError(error, 'Hiba az allokáció során');
    }
  };

  const handleViewAllocations = async (cost: Cost) => {
    try {
      setSelectedCost(cost);
      const response = await costApi.getAllocations(cost.id);
      setAllocations(response.allocations);
      setShowAllocationModal(true);
    } catch (error) {
      console.error('Error loading allocations:', error);
      handleApiError(error, 'Hiba az allokációk betöltése során');
    }
  };

  const handleDeleteAllocations = async (cost: Cost) => {
    try {
      await costApi.deleteAllocations(cost.id);
      showSuccess('Allokációk törölve');
      setShowAllocationModal(false);
      loadCosts();
    } catch (error) {
      console.error('Error deleting allocations:', error);
      handleApiError(error, 'Hiba az allokációk törlése során');
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    const { action, id } = confirmAction;
    try {
      switch (action) {
        case 'approve':
          await handleApproveCost(id);
          break;
        case 'delete':
          await handleDeleteCost(id);
          break;
        case 'allocate':
          await executeAllocateCost(id);
          break;
        case 'deleteAllocations': {
          const cost = costs.find(c => c.id === id);
          if (cost) await handleDeleteAllocations(cost);
          break;
        }
      }
    } finally {
      setConfirmAction(null);
    }
  };

  const confirmModalConfig: Record<string, { title: string; message: string; confirmLabel: string; variant: 'danger' | 'warning' }> = {
    approve: {
      title: 'Költség jóváhagyása',
      message: 'Biztosan jóváhagyja ezt a költséget?',
      confirmLabel: 'Jóváhagyás',
      variant: 'warning',
    },
    delete: {
      title: 'Költség törlése',
      message: 'Biztosan törölni szeretnéd ezt a költséget?',
      confirmLabel: 'Törlés',
      variant: 'danger',
    },
    allocate: {
      title: 'Költség allokálása',
      message: 'Biztosan allokálja ezt a költséget az állatokhoz?',
      confirmLabel: 'Allokálás',
      variant: 'warning',
    },
    deleteAllocations: {
      title: 'Allokációk törlése',
      message: 'Biztosan törli az allokációkat? Ezután újra allokálhatja a költséget.',
      confirmLabel: 'Törlés',
      variant: 'danger',
    },
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('hu-HU');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
    }).format(amount);
  };

  const getStatusColor = (cost: Cost) => {
    return cost.approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';
  };

  const getStatusText = (cost: Cost) => {
    return cost.approved ? t('approved', 'costs') : t('waiting', 'costs');
  };

  const getLevelText = (level: string) => {
    switch (level) {
      case 'farm': return t('farm', 'costs');
      case 'box': return t('box', 'costs');
      case 'animal': return t('animal', 'costs');
      default: return level;
    }
  };

  const getAllocationText = (method: string) => {
    switch (method) {
      case 'darabszam': return t('by_quantity', 'costs');
      case 'suly': return t('by_weight', 'costs');
      case 'egyedi': return t('individual', 'costs');
      default: return method;
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-128px)]">
      <div className="mb-4 flex-shrink-0">
        <h1 className="text-3xl font-bold text-gray-900">{t('costs_management', 'costs')}</h1>
        <p className="mt-2 text-gray-600">
          {t('costs_management_desc', 'costs')}
        </p>
      </div>

      {activePeriod && (
        <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4 flex-shrink-0">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                <span className="font-medium">Aktív időszak: </span>
                {activePeriod.name}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-4 items-center flex-shrink-0">
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
        >
          + {t('add', 'action')}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-4 flex-shrink-0">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('period', 'costs') || 'Időszak'}
            </label>
            <ClearableSelect
              value={filters.period_id?.toString() || ''}
              onChange={(val) => setFilters({ ...filters, period_id: val ? parseInt(val) : undefined })}
              placeholder={t('all_periods', 'costs') || 'Összes időszak'}
              disabled={loading}
            >
              {periods.map((period) => (
                <option key={period.id} value={period.id}>{period.name}</option>
              ))}
            </ClearableSelect>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('farm', 'costs')}
            </label>
            <ClearableSelect
              value={filters.farm_id?.toString() || ''}
              onChange={(val) => setFilters({ ...filters, farm_id: val ? parseInt(val) : undefined })}
              placeholder={t('all_farms', 'costs') || 'Összes telep'}
              disabled={loading}
            >
              {farms.map((farm) => (
                <option key={farm.id} value={farm.id}>{farm.name}</option>
              ))}
            </ClearableSelect>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('category', 'costs')}
            </label>
            <ClearableSelect
              value={filters.category || ''}
              onChange={(val) => setFilters({ ...filters, category: val || undefined })}
              placeholder={t('all_categories', 'costs') || 'Összes kategória'}
              disabled={loading}
            >
              <option value="takarmany">{t('feed', 'costs')}</option>
              <option value="gyogyszer">{t('medicine', 'costs')}</option>
              <option value="allatorvos">{t('veterinarian', 'costs')}</option>
              <option value="szallitas">{t('transport', 'costs')}</option>
              <option value="rezsi">{t('utilities', 'costs')}</option>
              <option value="gepkarbantartas">{t('machine_maintenance', 'costs')}</option>
              <option value="biztositas">{t('insurance', 'costs')}</option>
              <option value="egyeb">{t('other', 'costs')}</option>
            </ClearableSelect>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('level', 'costs')}
            </label>
            <ClearableSelect
              value={filters.cost_level || ''}
              onChange={(val) => setFilters({ ...filters, cost_level: (val as 'farm' | 'box' | 'animal') || undefined })}
              placeholder={t('all_levels', 'costs') || 'Összes szint'}
              disabled={loading}
            >
              <option value="farm">{t('farm', 'costs')}</option>
              <option value="box">{t('box', 'costs')}</option>
              <option value="animal">{t('animal', 'costs')}</option>
            </ClearableSelect>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('status', 'costs')}
            </label>
            <ClearableSelect
              value={filters.approved !== undefined ? (filters.approved ? '1' : '0') : ''}
              onChange={(val) => setFilters({ ...filters, approved: val !== '' ? val === '1' : undefined })}
              placeholder={t('all_statuses', 'costs') || 'Összes státusz'}
              disabled={loading}
            >
              <option value="1">{t('approved', 'costs')}</option>
              <option value="0">{t('waiting', 'costs')}</option>
            </ClearableSelect>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('date_from', 'common') || 'Date From'}
            </label>
            <input
              type="date"
              value={filters.from_date || ''}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('date_to', 'common') || 'Date To'}
            </label>
            <input
              type="date"
              value={filters.to_date || ''}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
      </div>

      {/* Add Cost Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('add_new_cost', 'costs')}</h3>
            <form onSubmit={handleAddCost} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Időszak</label>
                  <select
                    value={newCost.period_id || activePeriod?.id || ''}
                    onChange={(e) => setNewCost({ ...newCost, period_id: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  >
                    <option value="">Nincs időszak</option>
                    {periods.map((period) => (
                      <option key={period.id} value={period.id}>
                        {period.name} {period.is_active && '(Aktív)'}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('level', 'costs')} *</label>
                  <select
                    required
                    value={newCost.cost_level}
                    onChange={(e) => setNewCost({ ...newCost, cost_level: e.target.value as 'farm' | 'box' | 'animal' })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  >
                    <option value="farm">{t('farm_level', 'costs')}</option>
                    <option value="box">{t('box_level', 'costs')}</option>
                    <option value="animal">{t('animal_specific', 'costs')}</option>
                  </select>
                </div>
                
                {newCost.cost_level === 'farm' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('farm', 'costs')}</label>
                    <select
                      value={newCost.farm_id || ''}
                      onChange={(e) => setNewCost({ ...newCost, farm_id: e.target.value ? parseInt(e.target.value) : undefined })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                    >
                      <option value="">{t('select_farm', 'costs')}</option>
                      {farms.map((farm) => (
                        <option key={farm.id} value={farm.id}>
                          {farm.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {newCost.cost_level === 'animal' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('animal', 'costs')}</label>
                    <div className="relative mt-1">
                      <div
                        onClick={() => setShowAnimalSelector(true)}
                        className={`block w-full px-3 py-2 border rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 ${
                          newCost.animal_id ? 'border-gray-300 text-gray-900' : 'border-gray-300 text-gray-400'
                        }`}
                      >
                        {animalDisplay || `${t('select_animal', 'costs')}...`}
                      </div>
                      {newCost.animal_id ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setNewCost(prev => ({ ...prev, animal_id: undefined }));
                            setAnimalDisplay('');
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      ) : null}
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('category', 'costs')} *</label>
                  <select
                    required
                    value={newCost.category}
                    onChange={(e) => setNewCost({ ...newCost, category: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  >
                    <option value="">{t('select_category', 'costs')}</option>
                    <option value="takarmany">{t('feed', 'costs')}</option>
                    <option value="gyogyszer">{t('medicine', 'costs')}</option>
                    <option value="allatorvos">{t('veterinarian', 'costs')}</option>
                    <option value="szallitas">{t('transport', 'costs')}</option>
                    <option value="rezsi">{t('utilities', 'costs')}</option>
                    <option value="gepkarbantartas">{t('machine_maintenance', 'costs')}</option>
                    <option value="biztositas">{t('insurance', 'costs')}</option>
                    <option value="egyeb">{t('other', 'costs')}</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('amount_huf', 'costs')} *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={newCost.amount}
                    onChange={(e) => setNewCost({ ...newCost, amount: parseFloat(e.target.value) || 0 })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('date', 'costs')} *</label>
                  <input
                    type="date"
                    required
                    value={newCost.cost_date}
                    onChange={(e) => setNewCost({ ...newCost, cost_date: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('allocation_method', 'costs')}</label>
                  <select
                    value={newCost.allocation_method}
                    onChange={(e) => setNewCost({ ...newCost, allocation_method: e.target.value as 'darabszam' | 'suly' | 'egyedi' })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  >
                    <option value="darabszam">{t('by_quantity', 'costs')}</option>
                    <option value="suly">{t('by_weight', 'costs')}</option>
                    <option value="egyedi">{t('individual', 'costs')}</option>
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">{t('description', 'costs')} *</label>
                  <input
                    type="text"
                    required
                    value={newCost.description}
                    onChange={(e) => setNewCost({ ...newCost, description: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                    placeholder={t('detailed_cost_description', 'costs')}
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">{t('invoice_number', 'costs')}</label>
                  <input
                    type="text"
                    value={newCost.invoice_number}
                    onChange={(e) => setNewCost({ ...newCost, invoice_number: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">{t('notes', 'costs')}</label>
                  <textarea
                    value={newCost.notes}
                    onChange={(e) => setNewCost({ ...newCost, notes: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('cancel', 'action')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  {t('add', 'action')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Cost Form Modal */}
      {showEditForm && editingCost && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Költség szerkesztése</h3>
            <form onSubmit={handleUpdateCost} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('category', 'costs')} *</label>
                  <select
                    required
                    value={editingCost.category}
                    onChange={(e) => setEditingCost({ ...editingCost, category: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">{t('select_category', 'costs')}</option>
                    <option value="takarmany">{t('feed', 'costs')}</option>
                    <option value="gyogyszer">{t('medicine', 'costs')}</option>
                    <option value="allatorvos">{t('veterinarian', 'costs')}</option>
                    <option value="szallitas">{t('transport', 'costs')}</option>
                    <option value="rezsi">{t('utilities', 'costs')}</option>
                    <option value="gepkarbantartas">{t('machine_maintenance', 'costs')}</option>
                    <option value="biztositas">{t('insurance', 'costs')}</option>
                    <option value="egyeb">{t('other', 'costs')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('amount_huf', 'costs')} *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={editingCost.amount}
                    onChange={(e) => setEditingCost({ ...editingCost, amount: parseFloat(e.target.value) || 0 })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('date', 'costs')} *</label>
                  <input
                    type="date"
                    required
                    value={editingCost.cost_date}
                    onChange={(e) => setEditingCost({ ...editingCost, cost_date: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('allocation_method', 'costs')}</label>
                  <select
                    value={editingCost.allocation_method}
                    onChange={(e) => setEditingCost({ ...editingCost, allocation_method: e.target.value as 'darabszam' | 'suly' | 'egyedi' })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="darabszam">{t('by_quantity', 'costs')}</option>
                    <option value="suly">{t('by_weight', 'costs')}</option>
                    <option value="egyedi">{t('individual', 'costs')}</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">{t('description', 'costs')} *</label>
                  <input
                    type="text"
                    required
                    value={editingCost.description}
                    onChange={(e) => setEditingCost({ ...editingCost, description: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder={t('detailed_cost_description', 'costs')}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">{t('invoice_number', 'costs')}</label>
                  <input
                    type="text"
                    value={editingCost.invoice_number || ''}
                    onChange={(e) => setEditingCost({ ...editingCost, invoice_number: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">{t('notes', 'costs')}</label>
                  <textarea
                    value={editingCost.notes || ''}
                    onChange={(e) => setEditingCost({ ...editingCost, notes: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditForm(false);
                    setEditingCost(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('cancel', 'action')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Mentés
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg flex flex-col flex-1 min-h-0">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {t('costs_list', 'costs')} ({total})
          </h3>
        </div>

        <div className="overflow-auto flex-1 min-h-0 relative">
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-60 z-10 flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
            </div>
          )}
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-[5]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('date', 'costs')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('category', 'costs')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('description', 'costs')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('amount', 'costs')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('level', 'costs')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('status', 'costs')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('actions', 'costs')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {costs.map((cost) => (
                <tr key={cost.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(cost.cost_date)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{cost.category}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{cost.description}</div>
                    {cost.invoice_number && (
                      <div className="text-xs text-gray-500">{t('invoice', 'costs')}: {cost.invoice_number}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatCurrency(cost.amount)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {getAllocationText(cost.allocation_method)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {getLevelText(cost.cost_level)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      {cost.is_allocated && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Allokálva
                        </span>
                      )}
                      {cost.approved && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {getStatusText(cost)}
                        </span>
                      )}
                      {!cost.approved && !cost.is_allocated && (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(cost)}`}>
                          {getStatusText(cost)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex flex-col gap-2">
                      {!cost.approved && (
                        <button
                          onClick={() => setConfirmAction({ action: 'approve', id: cost.id })}
                          className="text-blue-600 hover:text-blue-900 text-left"
                        >
                          {t('approve', 'action')}
                        </button>
                      )}
                      {!cost.is_allocated && cost.allocation_method !== 'egyedi' && (
                        <button
                          onClick={() => handleAllocateCost(cost)}
                          className="text-green-600 hover:text-green-900 text-left"
                        >
                          Allokál
                        </button>
                      )}
                      {cost.is_allocated && (
                        <button
                          onClick={() => handleViewAllocations(cost)}
                          className="text-purple-600 hover:text-purple-900 text-left"
                        >
                          Allokációk
                        </button>
                      )}
                      <button
                        onClick={() => handleEditCost(cost)}
                        className="text-indigo-600 hover:text-indigo-900 text-left"
                      >
                        {t('edit', 'action')}
                      </button>
                      <button
                        onClick={() => setConfirmAction({ action: 'delete', id: cost.id })}
                        className="text-red-600 hover:text-red-900 text-left"
                      >
                        {t('delete', 'action')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {costs.length === 0 && !loading && (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">{t('no_costs', 'costs')}</h3>
              <p className="mt-1 text-sm text-gray-500">
                {t('costs_will_appear_here', 'costs')}
              </p>
            </div>
          )}
        </div>

        {lastPage > 1 && (
          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 flex-shrink-0">
            <div className="text-sm text-gray-700">
              {((currentPage - 1) * 50) + 1}–{Math.min(currentPage * 50, total)} / {total}
            </div>
            <Pagination page={currentPage} lastPage={lastPage} onPageChange={setCurrentPage} disabled={loading} />
          </div>
        )}
      </div>

      {/* Summary Section */}
      {costs.length > 0 && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 flex-shrink-0">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {t('approved_costs', 'costs')}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatCurrency(costs.filter(c => c.approved).reduce((sum, c) => sum + c.amount, 0))}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {t('waiting_for_approval', 'costs')}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatCurrency(costs.filter(c => !c.approved).reduce((sum, c) => sum + c.amount, 0))}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {t('total_costs', 'costs')}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatCurrency(costs.reduce((sum, c) => sum + c.amount, 0))}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Animal Selector Modal */}
      <AnimalSelectorModal
        isOpen={showAnimalSelector}
        onClose={() => setShowAnimalSelector(false)}
        onSelect={handleAnimalSelected}
        mode="single"
        title={t('select_animal', 'costs') || 'Select animal'}
      />

      {/* Allocation Modal */}
      {showAllocationModal && selectedCost && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Költség allokációk - {selectedCost.description}
              </h3>
              <button
                onClick={() => setShowAllocationModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Teljes összeg:</span>
                  <p className="font-semibold">{formatCurrency(selectedCost.amount)}</p>
                </div>
                <div>
                  <span className="text-gray-600">Allokált összeg:</span>
                  <p className="font-semibold">
                    {formatCurrency(allocations.reduce((sum, a) => sum + parseFloat(a.allocated_amount.toString()), 0))}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">Állatok száma:</span>
                  <p className="font-semibold">{allocations.length}</p>
                </div>
                <div>
                  <span className="text-gray-600">Felülírt:</span>
                  <p className="font-semibold">{allocations.filter(a => a.is_manual_override).length}</p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto mb-4">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Állat
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Allokált összeg
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Súly (kg)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Státusz
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {allocations.map((allocation) => (
                    <tr key={allocation.id} className={allocation.is_manual_override ? 'bg-yellow-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {allocation.animal?.ear_tag || `Állat #${allocation.animal_id}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(parseFloat(allocation.allocated_amount.toString()))}
                        {allocation.original_allocated_amount && (
                          <span className="text-xs text-gray-500 ml-2">
                            (Eredeti: {formatCurrency(parseFloat(allocation.original_allocated_amount.toString()))})
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {allocation.allocation_weight ? `${allocation.allocation_weight} kg` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {allocation.is_manual_override ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Felülírva
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Automatikus
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setConfirmAction({ action: 'deleteAllocations', id: selectedCost.id })}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Allokációk törlése
              </button>
              <button
                onClick={() => setShowAllocationModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Bezár
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmAction && (
        <ConfirmModal
          isOpen={!!confirmAction}
          onConfirm={handleConfirmAction}
          onCancel={() => setConfirmAction(null)}
          title={confirmModalConfig[confirmAction.action].title}
          message={confirmModalConfig[confirmAction.action].message}
          confirmLabel={confirmModalConfig[confirmAction.action].confirmLabel}
          variant={confirmModalConfig[confirmAction.action].variant}
        />
      )}
    </div>
  );
};

export default Costs;